import { Button, Card, CardHeader, cn, Skeleton, Stat } from "@kontaktfix/ui";
import {
  AddressBookIcon,
  ArrowRightIcon,
  CheckIcon,
  ListChecksIcon,
  TrayIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AuditLine } from "@/components/history";
import { Page, PageHeader } from "@/components/page";
import { formatNumber } from "@/lib/i18n";
import { useActivity, useContacts, useLists, useMe, useSyncOverview } from "@/lib/queries";

const PHONE_KEY = "kf-phone-guide-seen";

function phoneGuideSeen(): boolean {
  try {
    return localStorage.getItem(PHONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markPhoneGuideSeen() {
  try {
    localStorage.setItem(PHONE_KEY, "1");
  } catch {
    // Not remembered in private mode; harmless.
  }
}

function Step({
  index,
  done,
  title,
  text,
  action,
}: {
  index: number;
  done: boolean;
  title: string;
  text: string;
  action: ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <span
        className={cn(
          "flex size-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold",
          done ? "border-success bg-success text-white" : "border-border-strong text-fg-muted",
        )}
      >
        {done ? <CheckIcon className="size-4" strokeWidth={3} /> : index}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "text-[13.5px] font-medium",
            done && "text-fg-muted line-through decoration-fg-subtle",
          )}
        >
          {title}
        </div>
        <div className="text-[12.5px] text-fg-muted">{text}</div>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: lists, isLoading: listsLoading } = useLists();
  const { data: contacts } = useContacts({ pageSize: 1 });
  const { data: sync } = useSyncOverview();
  const { data: activity } = useActivity({ pageSize: 6 });
  const isAdmin = me?.role === "admin";
  const firstList = lists?.[0];

  const connected = sync?.connectionStatus === "granted";
  const steps = [
    {
      key: "connect",
      done: Boolean(connected),
      to: "/settings/microsoft",
      adminOnly: true,
    },
    { key: "lists", done: Boolean(lists?.length), to: "/lists", adminOnly: true },
    { key: "contacts", done: Boolean(contacts?.total), to: "/import" },
    {
      key: "assign",
      done: Boolean(lists?.some((l) => l.assignmentCount > 0)),
      to: firstList ? `/lists/${firstList.id}?tab=assignments` : "/lists",
      adminOnly: true,
    },
    { key: "sync", done: Boolean(sync?.deliveredCount), to: "/sync" },
    { key: "phone", done: phoneGuideSeen(), to: "/help/phone" },
  ] as const;
  const allDone = steps.every((s) => s.done);

  return (
    <Page>
      <PageHeader
        title={t("home.welcome", { name: me?.identity.name.split(" ")[0] })}
        description={t("home.subtitle")}
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t("home.stats.contacts")}
          value={contacts ? formatNumber(contacts.total) : "-"}
          icon={<AddressBookIcon />}
        />
        <Stat
          label={t("home.stats.lists")}
          value={lists ? formatNumber(lists.length) : "-"}
          icon={<ListChecksIcon />}
        />
        <Stat
          label={t("home.stats.recipients")}
          value={sync ? formatNumber(sync.recipientCount) : "-"}
          icon={<UsersIcon />}
        />
        <Stat
          label={t("home.stats.delivered")}
          value={sync ? formatNumber(sync.deliveredCount) : "-"}
          icon={<TrayIcon />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        {!allDone && (
          <Card>
            <CardHeader title={t("home.gettingStarted")} />
            {listsLoading ? (
              <div className="p-5">
                <Skeleton className="h-40" />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {steps.map((step, i) => (
                  <Step
                    key={step.key}
                    index={i + 1}
                    done={step.done}
                    title={t(`home.steps.${step.key}.title`)}
                    text={t(`home.steps.${step.key}.text`)}
                    action={
                      step.done ? (
                        <span className="text-[12px] font-medium text-success">{t("home.done")}</span>
                      ) : "adminOnly" in step && step.adminOnly && !isAdmin ? null : (
                        <Button size="sm" asChild>
                          <a href={step.to}>
                            {t(`home.steps.${step.key}.action`)}
                            <ArrowRightIcon />
                          </a>
                        </Button>
                      )
                    }
                  />
                ))}
              </div>
            )}
          </Card>
        )}
        <Card className={allDone ? "lg:col-span-2" : undefined}>
          <CardHeader
            title={t("home.recent")}
            actions={
              <Link to="/activity" className="text-[12.5px] text-fg-muted hover:text-fg">
                {t("home.allActivity")}
              </Link>
            }
          />
          <div className="divide-y divide-border px-5">
            {activity?.items.map((entry) => (
              <AuditLine key={entry.id} entry={entry} />
            ))}
            {!activity?.items.length && (
              <p className="py-4 text-[13px] text-fg-muted">{t("activity.empty")}</p>
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
